import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  UserCheck,
  MonitorSmartphone,
  BarChart3,
  ShieldCheck,
  Database,
  Rocket,
  ArrowRight,
  Star,
  Quote,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const staffing = [
  {
    icon: Briefcase,
    title: "Managed Solutions",
    desc: "Customized project-based consultants and solutions for your ongoing or new projects.",
  },
  {
    icon: UserCheck,
    title: "Salaried Professional Service",
    desc: "Dedicated long-term consultants with full-time positions on contractual basis.",
  },
  {
    icon: MonitorSmartphone,
    title: "Digital Projects",
    desc: "Complex web and mobile experts for design and development.",
  },
  {
    icon: BarChart3,
    title: "Data Analytics & Visualization",
    desc: "Skilled Data and Visualization experts for popular tools like Power BI, Tableau & more.",
  },
  {
    icon: ShieldCheck,
    title: "Data Security & Privacy",
    desc: "Technical resources specialized in keeping your data secure in any environment.",
  },
  {
    icon: Database,
    title: "Enterprise Resource Planning (ERP)",
    desc: "ERP support specialists for a diverse set of software and industry scenarios.",
  },
];

const testimonials = [
  {
    quote:
      "Millennium Infotech provided us with project-based consultants who were nothing short of exceptional. Their expertise was evident in the swift turnaround and thorough understanding of our project needs. Partnering with them allowed us to meet our project goals ahead of schedule and set new standards for quality in our industry.",
    name: "Mary J. Hollister",
    role: "Entrepreneur",
  },
  {
    quote:
      "Thanks to Millennium Infotech, we transformed our data processing capabilities. Their data experts customized solutions using Power BI that enabled us to gain deeper insights and make data-driven decisions more effectively than ever before. The impact on our operational efficiency has been tremendous.",
    name: "Roy Phipps",
    role: "Marketing Manager",
  },
  {
    quote:
      "We faced numerous challenges with our ERP implementation until we engaged the specialists from Millennium Infotech. Their team not only stabilized our systems but also optimized our processes across multiple departments. Their ongoing support and expertise have been crucial in maintaining our ERP systems to adapt to our evolving business needs.",
    name: "Paul N. Jeter",
    role: "Developer",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const ProfessionalServices = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.professionalServices} jsonLd={[buildBreadcrumbJsonLd(seoConfig.professionalServices.crumbs)]} />
      <PageHero
        title="Professional Services"
        crumbs={[{ label: "Home", href: "/" }, { label: "Professional Services" }]}
        lightfall
      />

      <section data-testid="ps-intro" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Expert Staffing Solutions
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
              Elevate Your Team with Top-Tier Talent
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Discover unparalleled professional services tailored to your business needs. Whether you're looking to
              enhance your team for long-term projects or need expert consultants for short-term objectives, our
              staffing solutions provide the perfect fit. Employ the industry's best to support your ongoing and
              upcoming initiatives, ensuring excellence and expertise right at your office. With our dedicated
              professionals, propel your projects to new heights.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/banner-attainica.jpg"
                alt="Consultants"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="staffing-solutions" className="pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-base md:text-lg font-medium text-slate-700">
              We carefully select our resources for their distinct talents.
            </p>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Staffing Solutions
            </h2>
            <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
            <p className="mt-5 text-base md:text-lg text-slate-500">
              Our expert consultants consistently deliver high-quality work worldwide.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {staffing.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -7 }}
                data-testid={`staffing-card-${i + 1}`}
                className="group text-center rounded-2xl border border-slate-200 bg-white p-9 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="mx-auto w-14 h-14 rounded-2xl bg-miti-blue/10 text-miti-blue flex items-center justify-center group-hover:bg-miti-blue group-hover:text-white transition-colors duration-300">
                  <s.icon size={24} strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 font-heading font-semibold text-lg text-slate-900 leading-snug">{s.title}</h3>
                <p className="mt-3 text-sm md:text-base text-slate-500 leading-relaxed">{s.desc}</p>
                <div className="mt-6 h-0.5 w-0 bg-miti-blue mx-auto transition-all duration-500 group-hover:w-16" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="ps-cta" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Empower Your Projects with Expert Resources
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Unlock the potential of your projects with our elite professional services. If your business requires
              expert resources for any duration, contact us today to explore how our tailored staffing solutions can
              seamlessly integrate with your needs and elevate your initiatives to success. Let's drive excellence
              together—reach out now!
            </p>
            <a
              href="/contact"
              data-testid="ps-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Rocket size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="https://images.pexels.com/photos/7993566/pexels-photo-7993566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Consult"
                className="w-full h-[360px] lg:h-[480px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="ps-testimonials" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Testimonials</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Word from Our Client
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-500">
              Discover how our staffing solutions have transformed businesses. These testimonials showcase the
              real-world impact and effectiveness of our professional services.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.name}
                {...fadeUp}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`ps-testimonial-${i + 1}`}
                className="rounded-xl bg-slate-50 border border-slate-100 p-8 flex flex-col"
              >
                <div className="flex gap-1" aria-label="5 star rating">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={15} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="mt-5 text-sm md:text-base text-slate-600 leading-relaxed flex-1">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-8 flex items-end justify-between gap-4">
                  <div>
                    <span className="block font-heading font-semibold text-slate-900">{t.name}</span>
                    <span className="block text-xs text-slate-400 mt-1">{t.role}</span>
                  </div>
                  <Quote size={40} className="text-miti-blue shrink-0" fill="currentColor" strokeWidth={0} />
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProfessionalServices;
